import * as React from 'react';
import gql from 'graphql-tag';
import I18n from 'i18n-js';
import classNames from 'classnames';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';

import { flashSuccess } from '@reverbdotcom/commons/src/flash';
import CreateOfferButton from '@reverbdotcom/commons/src/offers/create_offer_button';
import { core_apimessages_Listing_ListingType as ListingType } from '@reverbdotcom/commons/src/rql';
import { GridViewListings as Data } from '../schemas/rql';
import { SHOP_PATH } from '@reverbdotcom/commons/src/url_helpers';
import { AddToCartButton, AddToCartButtonFragment } from '@reverbdotcom/commons/src/components/add_to_cart_button';
import ConditionDisplay from '@reverbdotcom/commons/src/components/condition_display';
import LazyImage from '@reverbdotcom/commons/src/components/lazy_image';
import SignupSigninModal from '@reverbdotcom/commons/src/components/signup_signin_modal';
import WatchBadge from '@reverbdotcom/commons/src/components/watch_badge';
import ListingCardNudge from '@reverbdotcom/commons/src/components/listing_card_nudge';
import { InOtherCartsCardNudge } from '@reverbdotcom/commons/src/components/in_other_carts_card_nudge';
import { RCButton } from '@reverbdotcom/commons/src/rc/RCButton';
import BumpTag, { shouldShowBumpTag } from '@reverbdotcom/commons/src/components/bump_tag';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { WEB } from '@reverbdotcom/commons/src/constants';
import { listingItemPagePath } from '@reverbdotcom/commons/src/url_helpers';
import { buildListingClickEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import ListingShippingDisplay from '@reverbdotcom/commons/src/components/listing_shipping_display';
import { PriceDisplay } from '@reverbdotcom/commons';
import { ListingCardFragment } from '@reverbdotcom/commons/src/components/listing_card';
import { RCStarRating } from '@reverbdotcom/commons/src/rc/RCStarRating';

const DIGITAL_LISTING_UUID = 'e71b6e43-7217-4746-b4e3-64bf708a2664';
const COMPONENT_NAME = 'GridCardRow';

interface Props {
  listing: Data.Fragment;
  loading: boolean;
  position?: number;
  trackingQuery?: string;
}

export default function GridCardRow(props: Props) {
  const user = useUser();
  const [modalOpen, setModalOpen] = React.useState(false);
  const [mobileDetailsExpanded, toggleMobileDetails] = React.useState(false);

  const isDigitalListing = !props.listing.categoryUuids ? false : props.listing.categoryUuids.includes(DIGITAL_LISTING_UUID);

  const listingClickEvent = () => {
    const sellerCountryCode = props.listing.shop.address?.country.countryCode;

    const isListingDomesticToBuyer = (sellerCountryCode?: string, buyerCountryCode?: string) => {
      if (!(sellerCountryCode && buyerCountryCode)) { return false; }

      return sellerCountryCode === buyerCountryCode;
    };

    return {
      query: props.trackingQuery,
      ...buildListingClickEvent({
        listing: props.listing,
        componentName: COMPONENT_NAME,
        displayStyle: 'Row',
        position: props.position,
        city: props.listing.shop.address?.locality,
        region: props.listing.shop.address?.region,
        countryCode: sellerCountryCode,
        isListingDomesticToBuyer: isListingDomesticToBuyer(sellerCountryCode, user?.countryCode),
      }),
    };
  };

  const isAuction = props.listing.listingType === ListingType.AUCTION;
  const buyerPriceCents = props.listing.pricing.buyerPrice.amountCents;

  const acceptsOffers = props.listing.listingType === ListingType.MAKE_AN_OFFER;
  const { name, slug } = props.listing.shop;
  const receivedCount = props.listing.seller?.feedbackSummary?.receivedCount;
  const rollingRatingPercentage = props.listing.seller?.feedbackSummary?.rollingRatingPercentage;
  const rating = rollingRatingPercentage / 100;

  const hasDiscount = !!props.listing.pricing.originalPrice;
  const discountRibbonText = props.listing.pricing.ribbon?.display;

  const freeExpeditedShipping = !!props.listing.shipping?.freeExpeditedShipping;
  const localPickupOnly = !!props.listing.shipping?.localPickupOnly;
  const openNewTab = user && user.deviceName === WEB;

  if (props.loading) return <div className={'grid-card-row--loading'} />;

  return (
    <div className={classNames(
      'grid-card-row',
      {
        'grid-card-row--expanded': mobileDetailsExpanded,
        'grid-card-row--loading': props.loading,
      },
    )}>
      <div className="grid-card-row__thumbnail">
        <CoreLink
          className="grid-card-row__thumbnail-inner"
          to={listingItemPagePath(props.listing)}
          target={openNewTab ? '_blank' : undefined}
          clickEvent={listingClickEvent()}
        >
          <LazyImage src={props.listing?.images[0]?.source} />
        </CoreLink>
        {(shouldShowBumpTag(props.listing)) && (
          <div className="grid-card-row__bump grid-card-row__bump--thumb">
            <BumpTag />
          </div>
        )}
      </div>
      <div className="grid-card-row__info">
        <div className="grid-card-row__titleblock">
          <div className="grid-card-row__titleblock-inner">
            {(shouldShowBumpTag(props.listing)) && (
              <div className="grid-card-row__bump grid-card-row__bump--titleblock">
                <BumpTag />
              </div>
            )}
            <CoreLink
              className="grid-card-row__title-inner"
              to={listingItemPagePath(props.listing)}
              target={openNewTab ? '_blank' : undefined}
              clickEvent={listingClickEvent()}
            >
              <h4 className="grid-card-row__title">
                {props.listing.title}
              </h4>
            </CoreLink>
            {!isDigitalListing &&
              <div className="grid-card-row__condition">
                <ConditionDisplay
                  slug={props.listing.condition.conditionSlug}
                  displayName={props.listing.condition.displayName}
                  textOnly
                />
              </div>
            }
          </div>
        </div>

        <div className="grid-card-row__priceblock">
          <div className="grid-card-row__priceblock-inner">
            {hasDiscount &&
              <div className="grid-card-row__original-price">
                <span className="grid-card-row__original-price-display">
                  {props.listing.pricing.originalPrice.display}
                </span>
                <span className="grid-card-row__discount">
                  {discountRibbonText}
                </span>
              </div>
            }
            <div className="grid-card-row__footer__pricing">
              <div className="grid-card-row__price">
                <PriceDisplay
                  amountCents={props.listing.pricing.buyerPrice.amountCents}
                  currency={props.listing.pricing.buyerPrice.currency}
                  display={props.listing.pricing.buyerPrice.display}
                  auction={isAuction}
                />
                <ListingShippingDisplay
                  listing={props.listing}
                  shopSubtotalForListingCents={buyerPriceCents}
                  showCombinedRate={false}
                  showBadges={false}
                  useCombinedShippingStrikethrough
                />
              </div>
            </div>
            <Nudges
              listing={props.listing}
              freeExpeditedShipping={freeExpeditedShipping}
              localPickupOnly={localPickupOnly}
            />
          </div>
        </div>

        <Nudges
          forMobile
          listing={props.listing}
          freeExpeditedShipping={freeExpeditedShipping}
          localPickupOnly={localPickupOnly}
        />

        <div className="grid-card-row__more-info">
          <RCButton
            variant="tertiary"
            size="small"
            onClick={() => toggleMobileDetails(true)}
          >
            {I18n.t('commons.gridCardRow.moreInfo')}
          </RCButton>
        </div>

        <div className="grid-card-row__shop-info">
          <div className="grid-card-row__shop-info-inner">
            <div className="grid-card-row__seller-name">
              <CoreLink
                to={SHOP_PATH.expand({ slug })}
                className="grid-card-row__seller-inner"
              >
                {name}
              </CoreLink>
            </div>
            <div className="grid-card-row__seller-location">
              {props.listing.shop.address.displayLocation}
            </div>
            {receivedCount > 0 && (
              <div className="grid-card-row__seller-rating">
                <div className="grid-card-row__seller-rating-text">
                  <span>{I18n.t('commons.gridCardRow.sellerRating')}</span>
                </div>
                <RCStarRating
                  rating={rating}
                  count={receivedCount}
                />
              </div>
            )}
          </div>
        </div>
        <div className="grid-card-row__actions">
          <div className="grid-card-row__actions-inner">
            <div className="grid-card-row__add-to-cart">
              <AddToCartButton
                listing={props.listing}
                parentComponentName={COMPONENT_NAME}
              />
            </div>
            {acceptsOffers &&
              <div className="grid-card-row__make-offer">
                <CreateOfferButton
                  listingId={props.listing.id}
                  listing={props.listing}
                  onOfferSuccess={(message) => {
                    flashSuccess(message);
                  }}
                  parentComponentName={COMPONENT_NAME}
                />
              </div>
            }
            <WatchBadge
              listing={props.listing}
              parentComponentName={COMPONENT_NAME}
              handleLoggedOutUser={() => setModalOpen(true)}
            />
            <SignupSigninModal
              onRequestClose={() => setModalOpen(false)}
              active={modalOpen}
              trackSource="WatchBadge"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// We don't want to display the "Free Shipping" nudge due to redundancy reasons in the Marketplace List View experiment,
// so explicitly passing in freeShipping={false} will omit that nudge.
function Nudges({ forMobile = false, freeExpeditedShipping, localPickupOnly, listing }) {
  const className = forMobile ? 'grid-card-row__mobile-nudges' : 'grid-card-row__nudges';

  return (
    <div className={className}>
      <ListingCardNudge
        freeExpeditedShipping={freeExpeditedShipping}
        freeShipping={false}
        localPickupOnly={localPickupOnly}
      />
      <InOtherCartsCardNudge listing={listing} />
    </div>
  );
}

export const GridCardRowFragment = gql`
  fragment GridViewListings on Listing {
    _id
    id
    categoryUuids
    otherBuyersWithListingInCartCounts
    shop {
      _id
      name
      slug
      preferredSeller
      quickShipper
      quickResponder
      address {
        locality
        region
        displayLocation
        country {
          countryCode
          name
        }
      }
    }
    seller {
      _id
      feedbackSummary {
        receivedCount
        rollingRatingPercentage
      }
    }
    ... AddToCartButtonData
    ... ListingCardFields
  }
  ${ListingCardFragment}
  ${AddToCartButtonFragment}
`;
